// components/Survey/ShareSurveyDialog.jsx — pick staff and share (or re-share) a survey with them
import React, { useMemo, useState } from "react";
import {
  Avatar, Button, Checkbox, Dialog, DialogSurface, DialogBody, DialogTitle, DialogContent, DialogActions,
  Field, Input, Spinner, Switch, Text, Textarea,
} from "@fluentui/react-components";
import { Search24Regular, Send24Regular, People24Regular, Mail24Regular, Dismiss20Regular, Info20Regular } from "@fluentui/react-icons";
import { useUserAccess } from "../../context/UserAccessContext";
import { useSurveys } from "../../context/SurveyContext";
import PeoplePicker from "../shared/PeoplePicker";
import { C } from "./surveyUI";
import { countQuestions, formatDate } from "./surveyUtils";

const EMAIL_RE = /[A-Z0-9._%+'-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;

function nameFromEmail(email) {
  return email.split("@")[0].split(/[._-]+/).filter(Boolean).map((p) => p[0].toUpperCase() + p.slice(1)).join(" ");
}

const SOURCES = [
  { key: "users", label: "App users", Icon: People24Regular },
  { key: "directory", label: "Directory search", Icon: Search24Regular },
  { key: "paste", label: "Paste emails", Icon: Mail24Regular },
];

/**
 * Props:
 *   surveyId   string
 *   preselect  string[] — emails to tick on open (e.g. re-share from the results page)
 *   onClose    () => void
 *   onDone     (message, type) => void
 */
export default function ShareSurveyDialog({ surveyId, preselect = [], onClose, onDone }) {
  const { users, isDemoMode } = useUserAccess();
  const { surveyById, assignments, responseByAssignment, shareSurvey } = useSurveys();
  const survey = surveyById[surveyId];

  const [source, setSource] = useState("users");
  const [search, setSearch] = useState("");
  const [dept, setDept] = useState("");
  const [extras, setExtras] = useState([]); // people added from directory / paste
  const [selected, setSelected] = useState(() => new Set(preselect.map((e) => e.toLowerCase())));
  const [picked, setPicked] = useState(null);
  const [pasteText, setPasteText] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [notify, setNotify] = useState(!isDemoMode);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(null); // { phase, n, total }

  // Everyone we can show: active app users + anyone added ad hoc
  const people = useMemo(() => {
    const map = new Map();
    users.filter((u) => u.isActive).forEach((u) => map.set(u.email.toLowerCase(), { email: u.email.toLowerCase(), name: u.name || u.email, department: u.department || "", source: "users" }));
    extras.forEach((p) => { if (!map.has(p.email)) map.set(p.email, p); });
    preselect.forEach((e) => {
      const email = e.toLowerCase();
      if (!map.has(email)) {
        const prev = assignments.find((a) => a.email === email);
        map.set(email, { email, name: prev?.name || nameFromEmail(email), department: prev?.department || "", source: "extra" });
      }
    });
    return [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [users, extras, preselect, assignments]);

  // Where each person stands on this survey
  const standing = useMemo(() => {
    const out = {};
    assignments.filter((a) => a.surveyId === surveyId).forEach((a) => {
      const prev = out[a.email] || { rounds: 0, pending: false, submitted: 0 };
      prev.rounds = Math.max(prev.rounds, a.round);
      if (responseByAssignment[a.id]) prev.submitted++;
      else prev.pending = true;
      out[a.email] = prev;
    });
    return out;
  }, [assignments, responseByAssignment, surveyId]);

  const departments = useMemo(() => [...new Set(people.map((p) => p.department).filter(Boolean))].sort(), [people]);

  const listed = people.filter((p) => {
    const q = search.trim().toLowerCase();
    return (!dept || p.department === dept) && (!q || p.name.toLowerCase().includes(q) || p.email.includes(q));
  });

  const toggle = (email) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(email)) next.delete(email); else next.add(email);
    return next;
  });

  const selectable = listed.filter((p) => !standing[p.email]?.pending);
  const allListedSelected = selectable.length > 0 && selectable.every((p) => selected.has(p.email));
  const toggleAll = () => setSelected((prev) => {
    const next = new Set(prev);
    selectable.forEach((p) => (allListedSelected ? next.delete(p.email) : next.add(p.email)));
    return next;
  });

  const addExtra = (list) => {
    setExtras((prev) => {
      const have = new Set(prev.map((p) => p.email));
      return [...prev, ...list.filter((p) => !have.has(p.email))];
    });
    setSelected((prev) => new Set([...prev, ...list.map((p) => p.email)]));
  };

  const addPicked = (u) => {
    if (!u?.email) return;
    addExtra([{ email: u.email.toLowerCase(), name: u.name || u.email, department: u.department || "", source: "directory" }]);
    setPicked(null);
  };

  const addPasted = () => {
    const found = [...new Set((pasteText.match(EMAIL_RE) || []).map((e) => e.toLowerCase()))];
    addExtra(found.map((email) => ({ email, name: nameFromEmail(email), department: "", source: "paste" })));
    setPasteText("");
    setSource("users");
    setSearch("");
  };

  const chosen = people.filter((p) => selected.has(p.email));
  const counts = chosen.reduce((acc, p) => {
    const s = standing[p.email];
    if (s?.pending) acc.skip++;
    else if (s) acc.reshare++;
    else acc.fresh++;
    return acc;
  }, { fresh: 0, reshare: 0, skip: 0 });
  const toSend = counts.fresh + counts.reshare;

  const submit = async () => {
    setBusy({ phase: "share", n: 0, total: chosen.length });
    try {
      const res = await shareSurvey(surveyId, chosen, {
        dueDate, notify, message,
        onProgress: (phase, n, total) => setBusy({ phase, n, total }),
      });
      const parts = [`Shared with ${res.created.length} staff`];
      if (res.skipped.length) parts.push(`${res.skipped.length} already had an open invitation`);
      if (notify) parts.push(`${res.email.sent} email${res.email.sent === 1 ? "" : "s"} sent`);
      const problems = res.failed.length + res.email.failed.length;
      if (problems) parts.push(`${problems} failed`);
      onDone?.(parts.join(" · "), problems ? "error" : "success");
      onClose();
    } catch (err) {
      onDone?.(err.message, "error");
      setBusy(null);
    }
  };

  if (!survey) return null;

  return (
    <Dialog open onOpenChange={(_, d) => !d.open && !busy && onClose()}>
      <DialogSurface style={{ maxWidth: 940, width: "94vw", padding: 0, overflow: "hidden" }}>
        <DialogBody style={{ gap: 0, display: "block" }}>
          <div style={{ background: `linear-gradient(135deg, ${C.navy}, ${C.navy2})`, padding: "20px 24px", color: "#fff" }}>
            <DialogTitle style={{ color: "#fff", fontSize: 18 }}>Share survey</DialogTitle>
            <Text style={{ color: "#C8D8FF", fontSize: 13 }}>
              {survey.title} · {countQuestions(survey.questions)} questions{survey.status === "draft" ? " · sharing will publish this draft" : ""}
            </Text>
          </div>

          <DialogContent style={{ padding: 0 }}>
            <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.5fr) minmax(280px, 1fr)", minHeight: 460 }}>
              {/* Recipients */}
              <div style={{ padding: "18px 22px", borderRight: `1px solid ${C.line}`, display: "flex", flexDirection: "column", minWidth: 0 }}>
                <div style={{ display: "flex", gap: 4, marginBottom: 14 }}>
                  {SOURCES.map(({ key, label, Icon }) => {
                    const on = source === key;
                    return (
                      <button key={key} type="button" onClick={() => setSource(key)} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 12px", borderRadius: 8, border: `1px solid ${on ? "#C7DBFF" : "transparent"}`, background: on ? "#F0F6FF" : "transparent", color: on ? C.brand : C.ink2, fontWeight: on ? 700 : 500, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>
                        <Icon style={{ fontSize: 16 }} /> {label}
                      </button>
                    );
                  })}
                </div>

                {source === "directory" && (
                  <div style={{ marginBottom: 14 }}>
                    <PeoplePicker value={picked} onChange={addPicked} placeholder="Search the LekOil directory by name or email…" />
                    <Text style={{ fontSize: 12, color: C.muted, display: "block", marginTop: 6 }}>
                      Staff who are not app users can still sign in to answer — they will only see their surveys.
                    </Text>
                  </div>
                )}

                {source === "paste" && (
                  <div style={{ marginBottom: 14 }}>
                    <Textarea value={pasteText} onChange={(_, d) => setPasteText(d.value)} rows={4} placeholder="Paste email addresses separated by commas, semicolons or new lines" style={{ width: "100%" }} />
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8 }}>
                      <Text style={{ fontSize: 12, color: C.muted }}>{(pasteText.match(EMAIL_RE) || []).length} email addresses detected</Text>
                      <Button size="small" appearance="primary" disabled={!(pasteText.match(EMAIL_RE) || []).length} onClick={addPasted}>Add to list</Button>
                    </div>
                  </div>
                )}

                <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                  <Input contentBefore={<Search24Regular style={{ fontSize: 16 }} />} value={search} onChange={(_, d) => setSearch(d.value)} placeholder="Filter people" style={{ flex: 1 }} size="small" />
                  <select value={dept} onChange={(e) => setDept(e.target.value)} style={{ border: `1px solid ${C.line}`, borderRadius: 6, fontSize: 13, padding: "0 8px", color: C.ink, background: "#fff" }}>
                    <option value="">All departments</option>
                    {departments.map((d) => <option key={d}>{d}</option>)}
                  </select>
                </div>

                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 4px", borderBottom: `1px solid ${C.line}` }}>
                  <Checkbox checked={allListedSelected ? true : selectable.some((p) => selected.has(p.email)) ? "mixed" : false} onChange={toggleAll} label={<span style={{ fontSize: 12, fontWeight: 600 }}>Select all shown ({selectable.length})</span>} />
                  <Text style={{ fontSize: 12, color: C.ink2 }}>{selected.size} selected</Text>
                </div>

                <div style={{ flex: 1, overflowY: "auto", maxHeight: 320, minHeight: 180 }}>
                  {listed.length === 0 && (
                    <div style={{ padding: 24, textAlign: "center", fontSize: 13, color: C.muted }}>
                      {people.length === 0 ? "No people yet — search the directory or paste emails." : "No one matches this filter."}
                    </div>
                  )}
                  {listed.map((p) => {
                    const s = standing[p.email];
                    const locked = s?.pending;
                    const chip = locked
                      ? { text: "Awaiting response", color: "#005A9E", bg: "#E3F0FF" }
                      : s ? { text: `Responded · round ${s.rounds + 1} next`, color: "#0B6A0B", bg: "#E7F4E4" } : null;
                    return (
                      <label key={p.email} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 4px", borderBottom: "1px solid #F5F4F2", cursor: locked ? "not-allowed" : "pointer", opacity: locked ? 0.65 : 1 }}>
                        <Checkbox checked={selected.has(p.email) && !locked} disabled={locked} onChange={() => toggle(p.email)} />
                        <Avatar name={p.name} size={28} color="colorful" />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: C.ink, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.name}</div>
                          <div style={{ fontSize: 11, color: C.muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.email}{p.department ? ` · ${p.department}` : ""}</div>
                        </div>
                        {p.source !== "users" && <span style={{ fontSize: 10, fontWeight: 700, color: "#5B3DC4", background: "#F1EDFF", padding: "2px 7px", borderRadius: 20 }}>SURVEY ONLY</span>}
                        {chip && <span style={{ fontSize: 10, fontWeight: 700, color: chip.color, background: chip.bg, padding: "2px 8px", borderRadius: 20, whiteSpace: "nowrap" }}>{chip.text}</span>}
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Options */}
              <div style={{ padding: "18px 22px", background: C.soft, display: "flex", flexDirection: "column", gap: 14 }}>
                <Field label="Respond by (optional)" hint={survey.settings?.closesAt ? `Survey closes ${formatDate(survey.settings.closesAt)}` : undefined}>
                  <Input type="date" value={dueDate} onChange={(_, d) => setDueDate(d.value)} max={survey.settings?.closesAt || undefined} />
                </Field>
                <Switch checked={notify} onChange={(_, d) => setNotify(d.checked)} label="Email each person their survey link" disabled={isDemoMode} />
                {notify && (
                  <Field label="Personal message (optional)">
                    <Textarea value={message} onChange={(_, d) => setMessage(d.value)} rows={4} placeholder="e.g. Please take 5 minutes to share your views before Friday." />
                  </Field>
                )}
                {isDemoMode && <Text style={{ fontSize: 12, color: C.muted }}>Emails are unavailable in demo mode.</Text>}

                <div style={{ marginTop: "auto", background: "#fff", border: `1px solid ${C.line}`, borderRadius: 10, padding: "12px 14px", fontSize: 13, lineHeight: 1.8, color: C.ink }}>
                  <div><strong>{counts.fresh}</strong> new invitation{counts.fresh === 1 ? "" : "s"}</div>
                  <div><strong>{counts.reshare}</strong> re-share{counts.reshare === 1 ? "" : "s"} <span style={{ color: C.muted }}>(they can respond again)</span></div>
                  {counts.skip > 0 && <div style={{ color: C.muted }}>{counts.skip} skipped — invitation still open</div>}
                  <div style={{ display: "flex", gap: 6, color: C.ink2, fontSize: 12, marginTop: 6, lineHeight: 1.5 }}>
                    <Info20Regular style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }} />
                    Each person can submit once per share. Sharing again after they respond opens a new round.
                  </div>
                </div>
              </div>
            </div>
          </DialogContent>

          <DialogActions style={{ padding: "14px 22px", borderTop: `1px solid ${C.line}` }}>
            {busy && (
              <Text style={{ fontSize: 13, color: C.ink2, marginRight: "auto", display: "flex", alignItems: "center", gap: 8 }}>
                <Spinner size="tiny" /> {busy.phase === "email" ? "Sending emails" : "Creating invitations"} {busy.n}/{busy.total}
              </Text>
            )}
            <Button onClick={onClose} disabled={!!busy} icon={<Dismiss20Regular />}>Cancel</Button>
            <Button appearance="primary" icon={<Send24Regular />} disabled={!toSend || !!busy} onClick={submit}>
              Share with {toSend} {toSend === 1 ? "person" : "people"}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
